import { useState, useRef, useEffect } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";
import { useSelector } from "react-redux";
import { FaSearchPlus, FaSearchMinus } from "react-icons/fa";
import { message } from "antd";
import { getOptionsByPath } from "../utilsdata";
import {ADD_EDIT_EMPLOYEE, BLOOD_GROUP, EMPLOYEE_DEPARTMENT} from "../Dropdownmanager";
import { ID_CARD,ROLE } from "../Dropdownmanager";
import { AntdButton, SelectInput } from "../blueplanitUtils/InputField";

const IDCard = ({ img }) => {
  const userData = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const NestedDatabucket = useSelector((state) => state.dataBucket.NestedDatabucket);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    id: "",
    role: "",
    phone: "",
    EmergencyContacts: "",
    bloodGroup: "",
    profileImage: "",
    Profile: "",
    department: "",
  });

  const [zoom, setZoom] = useState(1);
  const [dragY, setDragY] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(null);
  const [startX, setStartX] = useState(null);

  const zoomRef = useRef(null);
  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.5));

  const handleMouseDown = (e) => {
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;

    setIsDragging(true);
    setStartX(clientX);
    setStartY(clientY);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;

    const deltaX = clientX - startX;
    const deltaY = clientY - startY;

    setStartX(clientX);
    setStartY(clientY);

    setDragX((prev) => prev + deltaX);
    setDragY((prev) => prev + deltaY);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setStartX(null);
    setStartY(null);
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleMouseMove);
    window.addEventListener("touchend", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleMouseMove);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, [isDragging, startX, startY]);

  const handleWheelZoom = (e) => {
    e.preventDefault();
    const zoomSpeed = 0.1;
    const direction = e.deltaY > 0 ? -1 : 1;
    setZoom((prev) => {
      const newZoom = prev + direction * zoomSpeed;
      return Math.min(Math.max(newZoom, 0.5), 2);
    });
  };
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const frontRef = useRef(null);

  const fields = [
    { name: "file", label: "Choose Image", type: "file" },
    { name: "Profile", label: "Enter Profile Name", type: "text" },
    { name: "phone", label: "Enter Phone", type: "text" },
    { name: "bloodGroup", label: "Enter Blood Group", type: "text" },
    { name: "firstName", label: "First Name", type: "text" },
    { name: "lastName", label: "Last Name", type: "text" },
    { name: "id", label: "Emp ID", type: "text" },
    { name: "EmergencyContacts", label: "Emergency Contact", type: "text" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "Profile" && value.length > 15) {
      setFormData((prevData) => ({
        ...prevData,
        Profile: prevData.firstName || prevData.lastName || "",
      }));
      message.error("Profile name too long, using first or last name instead.");
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prevData) => ({
        ...prevData,
        profileImage: URL.createObjectURL(file),
      }));
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setIsFormSubmitted(true);
  };

  const handleClose = (e) => {
    e.preventDefault();

    setFormData({
      firstName: userData?.firstname || "",
      lastName: userData?.lastName || "",
      id: "",
      role: "",
      phone: userData?.mobile || "",
      EmergencyContacts: userData?.alternativeNumber || "",
      bloodGroup: "",
      profileImage: userData?.image || "",
      Profile: "",
      department: "",
    });
  };

  const handleDownload = async () => {
    const frontElement = frontRef.current;
    const zoomElement = zoomRef.current;
    if (!frontElement) return;

    const scale = 5; // export resolution multiplier only - not a layout scale
    const frontRect = frontElement.getBoundingClientRect();
    const scaledRect = (el) => {
      const r = el.getBoundingClientRect();
      return {
        x: (r.left - frontRect.left) * scale,
        y: (r.top - frontRect.top) * scale,
        width: r.width * scale,
        height: r.height * scale,
      };
    };

    // html2canvas cannot render two CSS features this card relies on:
    //  - `writing-mode` (the rotated Name/Department labels) - unsupported,
    //    so a direct capture silently drops the vertical text.
    //  - `object-fit` (the profile photo) - unsupported, so the raw image
    //    would be stretched to fill its box instead of preserving aspect
    //    ratio like the browser does.
    // These three elements are hidden with `visibility:"hidden"` (which,
    // unlike `display:"none"`, keeps their layout space so nothing else on
    // the card reflows/shifts during capture) and redrawn afterwards using
    // their real measured position/size. Everything else on the card is
    // captured exactly as rendered.
    const nameTextEl = frontElement.querySelector(".horizontal-text");
    const deptTextEl = frontElement.querySelector(".profile");
    const photoWrapperEl = frontElement.querySelector(".ProfileImageCircleWrapper");
    const photoContainerEl = frontElement.querySelector(".ProfileImageContainer");
    const photoImgEl = frontElement.querySelector(".ProfileImage");

    const captureLabel = (el) =>
      el && { text: el.textContent, rect: scaledRect(el), style: getComputedStyle(el) };

    const nameOverlay = captureLabel(nameTextEl);
    const deptOverlay = captureLabel(deptTextEl);
    const photoOverlay =
      photoWrapperEl && photoContainerEl && photoImgEl?.complete && photoImgEl.naturalWidth
        ? {
            circle: scaledRect(photoWrapperEl),
            container: scaledRect(photoContainerEl),
            img: photoImgEl,
          }
        : null;

    if (zoomElement) zoomElement.style.visibility = "hidden";
    if (nameTextEl) nameTextEl.style.visibility = "hidden";
    if (deptTextEl) deptTextEl.style.visibility = "hidden";
    if (photoWrapperEl) photoWrapperEl.style.visibility = "hidden";
    await new Promise((r) => setTimeout(r, 100));

    const combinedCanvas = await html2canvas(frontElement, { scale });

    if (zoomElement) zoomElement.style.visibility = "visible";
    if (nameTextEl) nameTextEl.style.visibility = "visible";
    if (deptTextEl) deptTextEl.style.visibility = "visible";
    if (photoWrapperEl) photoWrapperEl.style.visibility = "visible";

    const ctx = combinedCanvas.getContext("2d");
    // html2canvas leaves its own internal scale/offset transform active on
    // the context it returns; reset it so our own coordinates (already in
    // final pixel space) aren't scaled/shifted a second time on top of it.
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    const drawVerticalLabel = (overlay) => {
      if (!overlay?.text?.trim()) return;
      const { text, rect, style } = overlay;
      ctx.save();
      ctx.fillStyle = style.color;
      ctx.font = `${style.fontWeight} ${parseFloat(style.fontSize) * scale}px ${style.fontFamily}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.translate(rect.x + rect.width / 2, rect.y + rect.height / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(text, 0, 0);
      ctx.restore();
    };

    drawVerticalLabel(nameOverlay);
    drawVerticalLabel(deptOverlay);

    if (photoOverlay) {
      const { circle, container, img } = photoOverlay;
      const centerX = circle.x + circle.width / 2;
      const centerY = circle.y + circle.height / 2;
      const radius = circle.width / 2;

      // Replicate `object-fit: contain`: fit the image inside the container
      // box preserving aspect ratio, centered (container is itself centered
      // inside the circular wrapper, so its center == the circle's center).
      const containScale = Math.min(
        container.width / img.naturalWidth,
        container.height / img.naturalHeight
      );
      const fitWidth = img.naturalWidth * containScale;
      const fitHeight = img.naturalHeight * containScale;

      // Then replicate `transform: translate(dragX,dragY) scale(zoom)`,
      // applied about that same center.
      const drawWidth = fitWidth * zoom;
      const drawHeight = fitHeight * zoom;
      const drawX = centerX + dragX * scale - drawWidth / 2;
      const drawY = centerY + dragY * scale - drawHeight / 2;

      ctx.save();
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
      ctx.restore();
    }

    const blob = await new Promise((resolve) =>
      combinedCanvas.toBlob(resolve, "image/jpeg", 1.0)
    );
    if (!blob) return;

    saveAs(blob, "id_card.jpg");

    const imgDataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });

    // Fit the PDF page to the canvas's actual aspect ratio instead of a
    // fixed [56, 89] box - forcing a mismatched box non-uniformly stretches
    // the image (addImage scales width/height independently), which is what
    // was pushing the photo/details lower and compressing the photo width.
    const pdfWidthMm = 56;
    const pdfHeightMm = pdfWidthMm * (combinedCanvas.height / combinedCanvas.width);

    const pdf = new jsPDF({
      orientation: pdfHeightMm >= pdfWidthMm ? "portrait" : "landscape",
      unit: "mm",
      format: [pdfWidthMm, pdfHeightMm],
    });

    pdf.addImage(imgDataUrl, "JPEG", 0, 0, pdfWidthMm, pdfHeightMm);
    pdf.save("id_card.pdf");
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleMouseMove);
    window.addEventListener("touchend", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleMouseMove);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, [isDragging, startY]);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div
          style={{ display: "flex", flexDirection: "column", flex: "1 1 auto" }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "space-evenly",
              gap: "10px",
              fontFamily: "Arial, sans-serif",
            }}
          >
            {/* Form Section */}
            <form onSubmit={handleFormSubmit}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "30px",
                }}
              >
                {/* Left Column */}
                <div style={{ width: "48%" }}>
                  {fields.slice(0, 5).map((field) => (
                    <div key={field?.name} style={{ marginBottom: "8px" }}>
                      <label
                        htmlFor={field?.name}
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          fontSize: "14px",
                        }}
                      >
                        {field?.label}
                      </label>
                      {field?.name === "bloodGroup" ? (
                        <SelectInput
                          id={field?.name}
                          name={field?.name}
                          value={formData[field?.name]}
                          onChange={(value) =>
                            handleInputChange({ target: { name: "bloodGroup", value } })
                          }
                          options={getOptionsByPath(NestedDatabucket, [ADD_EDIT_EMPLOYEE, BLOOD_GROUP])}
                          required
                          style={{
                            width: "100%",
                          }}
                        />
                      ) : (
                        <input
                          id={field?.name}
                          type={field?.type}
                          name={field?.name}
                          placeholder={field?.label}
                          value={formData[field?.name]}
                          onChange={
                            field?.type === "file"
                              ? handleImageChange
                              : handleInputChange
                          }
                          required
                          style={{
                            width: "100%",
                            padding: "6px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Right Column */}
                <div style={{ width: "48%" }}>
                  {fields.slice(5).map((field) => (
                    <div key={field?.name} style={{ marginBottom: "10px" }}>
                      <label
                        htmlFor={field?.name}
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          fontSize: "14px",
                        }}
                      >
                        {field?.label}
                      </label>
                      <input
                        id={field?.name}
                        type={field?.type}
                        name={field?.name}
                        placeholder={field?.label}
                        value={formData[field?.name]}
                        onChange={
                          field?.type === "file"
                            ? handleImageChange
                            : handleInputChange
                        }
                        required
                        style={{
                          width: "100%",
                          padding: "6px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                    </div>
                  ))}

                  {/* Role Dropdown */}
                  <div style={{ marginBottom: "10px" }}>

                    <SelectInput
                      name="role"
                      id="team-select"
                      label='Role'
                      className="form-control"
                      onChange={(value) =>
                        handleInputChange({ target: { name: "role", value } })
                      }
                      options={getOptionsByPath(NestedDatabucket, [ID_CARD, ROLE])}
                      value={formData.role || ""}
                      required
                      style={{
                        width: "100%",
                      }}
                    />
                  </div>

                  {/* Department Dropdown */}
                  <div style={{ marginBottom: "10px" }}>
                    <SelectInput
                      name="department"
                      id="department-select"
                      label="Department"
                      className="form-control"
                      onChange={(value) =>
                        handleInputChange({ target: { name: "department", value } })
                      }
                      options={getOptionsByPath(NestedDatabucket, [ADD_EDIT_EMPLOYEE, EMPLOYEE_DEPARTMENT])}
                      value={formData.department || ""}
                      required
                      style={{
                        width: "100%",
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginTop: "10px", display: "flex", gap: "5px" }}>
                <AntdButton
                  type="submit"
                  onClick={handleFormSubmit}
                  style={{
                    width: "23%",
                    padding: "10px 20px",
                    backgroundColor: "#007bff",
                    color: "#fff",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                  label='Submit'
                />

                <AntdButton
                  type="button"
                  onClick={handleClose}
                  style={{
                    width: "23%",
                    padding: "10px 20px",
                    backgroundColor: "#404142ff",
                    color: "#fff",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                  label='Clear'
                />
              </div>
            </form>

            {/* ID Card Section */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              <div
                className="id-card"
                style={{
                  perspective: "1000px",
                  width: "300px",
                  height: "400px",
                  position: "relative",
                }}
              >
                <div
                  className="card-inner"
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    transformStyle: "preserve-3d",
                    transition: "transform 0.8s",
                  }}
                >
                  {/* Front Side */}
                  <div
                    ref={frontRef}
                    className="front-side"
                    style={{
                      width: "100%",
                      height: "100%",
                      position: "absolute",
                      backfaceVisibility: "hidden",
                      backgroundColor: "#fff",
                      borderRadius: "10px",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      padding: "10px",
                    }}
                  >
                    <div>
                      <h6
                        className="horizontal-text"
                        style={{
                          fontSize: "36px",
                          marginLeft: "10px",
                          fontWeight: "bold",
                          color: "gray",
                          display: "flex",
                          textAlign: "center",
                          writingMode: "vertical-rl",
                          transform: "rotate(180deg)",
                        }}
                      >
                        {
                          (() => {
                            const profile = formData?.Profile?.trim();
                            const firstName = formData?.firstName?.trim();
                            const lastName = formData?.lastName?.trim();

                            if (profile && profile.length <= 15) {
                              return profile.toUpperCase();
                            }

                            if (firstName && firstName.length <= 15) {
                              return firstName.toUpperCase();
                            }

                            if (lastName && lastName.length <= 15) {
                              return lastName.toUpperCase();
                            }

                            return "PROFILE";
                          })()
                        }
                      </h6>
                    </div>
                    <div
                      className="BlueBoxWrapper"
                      style={{
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        className="BlueBox"
                        style={{
                          width: "137px",
                          height: "200px",
                          overflow: "hidden",
                          marginLeft: "100px",
                          marginBottom: "70px",
                          marginTop: "15px",
                          backgroundColor: "rgba(108, 206, 255, 0.77)",
                        }}
                      >
                        <img
                          className="InnerBlueBoxImage"
                          src="./blueplanit.png"
                          alt="Profile"
                          style={{
                            marginLeft: "39px",
                            marginTop: "25px",
                            objectFit: "cover",
                            display: "flex",
                            width: "75px",
                            height: "65px",
                            transform: "scale(0.8)",
                          }}
                        />

                        <p
                          className="profile"
                          style={{
                            marginLeft: "110px",
                            writingMode: "vertical-rl",
                            fontWeight: 550,
                            transform: "rotate(180deg)",
                            marginTop: "5px",
                            color: "rgba(4, 16, 58, 0.77)",
                            height: "90px",
                          }}
                        >
                          {formData?.department?.toUpperCase()}
                        </p>
                      </div>

                      <div
                        className="ProfileImageCircleWrapper"
                        style={{
                          width: "170px",
                          height: "170px",
                          borderRadius: "50%",
                          overflow: "hidden",
                          marginTop: "-165px",
                          marginLeft: "20px",
                          marginBottom: "10px",
                          zIndex: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <div
                          className="ProfileImageContainer"
                          onWheel={handleWheelZoom}
                          style={{
                            overflow: "hidden",
                            width: "200px",
                            height: "200px",
                            position: "relative",
                          }}
                        >
                          <img
                            className="ProfileImage"
                            src={
                              formData?.profileImage ||
                              "./profile.png"
                            }
                            alt="Profile"
                            onMouseDown={handleMouseDown}
                            onTouchStart={handleMouseDown}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              transform: `translate(${dragX}px, ${dragY}px) scale(${zoom})`,
                              cursor: isDragging ? "grabbing" : "grab",
                              transition: isDragging ? "none" : "transform 0.3s ease",
                              margin: "auto",
                            }}
                          />

                        </div>
                      </div>

                      <div
                        style={{
                          ref: { zoomRef },
                          className: "zoomElement",
                          display: "flex",
                          justifyContent: "center",
                          gap: "10px",
                          marginTop: "5px",
                          marginLeft: "50px",
                        }}
                      >
                        <div ref={zoomRef}>
                          <AntdButton
                            type="button"
                            onClick={handleZoomOut}
                            style={{
                              padding: "4px 8px",
                              fontSize: "8px",
                              borderRadius: "4px",
                              border: "1px solid #ccc",
                              cursor: "pointer",
                            }}
                            label={<FaSearchMinus size={10} />}
                          />
                          <AntdButton
                            type="button"
                            onClick={handleZoomIn}
                            style={{
                              padding: "4px 8px",
                              fontSize: "8px",
                              borderRadius: "4px",
                              border: "1px solid #ccc",
                              cursor: "pointer",
                            }}
                            label={<FaSearchPlus size={10} />}

                          />
                        </div>
                      </div>

                      <div
                        className="user-details"
                        style={{ padding: "1px 50px 20px 35px" }}
                      >
                        <p
                          style={{
                            fontSize: "8px",
                            margin: 0,
                            fontWeight: "700",
                          }}
                        >
                          EMP ID:{" "}
                          <span style={{ fontWeight: "400" }}>
                            {formData.id || "ID"}
                          </span>
                        </p>
                        <p
                          style={{
                            fontSize: "8px",
                            margin: 0,
                            fontWeight: "700",
                          }}
                        >
                          FULL NAME:{" "}
                          <span
                            style={{
                              fontWeight: "400",
                              width: "100px",
                              overflow: "hidden",
                            }}
                          >{`${formData.firstName?.toUpperCase()} ${formData.lastName?.toUpperCase()}`}</span>
                        </p>
                        <p
                          style={{
                            fontSize: "8px",
                            margin: 0,
                            fontWeight: "700",
                          }}
                        >
                          ROLE:{" "}
                          <span style={{ fontWeight: "400" }}>
                            {formData.role || "Role"}
                          </span>
                        </p>
                        <p
                          style={{
                            fontSize: "8px",
                            margin: 0,
                            fontWeight: "700",
                          }}
                        >
                          PHONE:{" "}
                          <span style={{ fontWeight: "400" }}>
                            {formData.phone || "Phone"}
                          </span>
                        </p>
                        <p
                          style={{
                            fontSize: "8px",
                            margin: 0,
                            fontWeight: "700",
                          }}
                        >
                          EMERGENCY CONTACT:{" "}
                          <span style={{ fontWeight: "400" }}>
                            {formData.EmergencyContacts || "N/A"}
                          </span>
                        </p>
                        <p
                          style={{
                            fontSize: "8px",
                            margin: 0,
                            fontWeight: "700",
                          }}
                        >
                          BLOOD:{" "}
                          <span style={{ fontWeight: "400" }}>
                            {formData.bloodGroup || "N/A"}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div>
                      <h6
                        className="line"
                        style={{
                          fontSize: "8px",
                          textAlign: "end",
                          marginBottom: "80px",
                          marginTop: "55px",
                          transform: "rotate(180deg)",
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%) rotate(90deg)",
                          marginRight: '-40px'
                        }}
                      >
                        ━━━━━━━━━━━
                      </h6>
                    </div>
                  </div>
                  {/* Back Side */}
                </div>
              </div>

              <AntdButton
                onClick={handleDownload}
                disabled={!isFormSubmitted}
                style={{
                  padding: "10px 20px",
                  backgroundColor: isFormSubmitted ? "#007bff" : "#ccc",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  cursor: isFormSubmitted ? "pointer" : "not-allowed",
                }}
                label='Download PDF/Img'
              />

            </div>

            {/* CSS for rotation */}
            <style>
              {`
    // .id-card:hover .card-inner {
    //   transform: rotateY(180deg);
    // }

      .back-side p {
        margin: 4px;
        font-size: 7px;
        padding:0 8px 2px 8px;
      }

  `}
            </style>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IDCard;
